// Custom Channels — per-brand engagement on external community / forum /
// Q&A platforms (Skool, Reddit, Quora, Indie Hackers, niche forums, …).
//
// This modal is opened from the brand-settings edit view ("Custom Channels"
// button). It mirrors the brand-settings editor: a list of platform cards,
// each opening an edit view with login info + post/reply URLs, a Topics tab
// listing crawler-found relevant threads (each with a reply composer that
// has an "AI draft" button next to "Send"), and a History tab for monitoring
// every reply we've posted.
//
// The worker does the heavy lifting: a daily per-platform crawl, a weekly
// internet-wide discovery of new platforms, AI reply drafting and posting.
// The UI just drives those actions through /api/custom-channels.
//
// The presentational subcomponents (ChannelCard, ChannelEditForm,
// ReplyComposer, ReplyHistoryList, TopicRow) are pure and exported so they
// can be unit-tested without redux/router/mdbreact context.
import React, { useEffect, useState, useCallback } from "react";
import { MDBModal, MDBModalBody, MDBModalHeader } from "mdbreact";
import addNotification from "helpers/notify";
import {
  listCustomChannels,
  createCustomChannel,
  updateCustomChannel,
  deleteCustomChannel,
  crawlCustomChannel,
  discoverCustomChannels,
  requestAiReply,
  sendReply,
  getCustomChannel,
} from "api/customChannels/customChannels";

export const PLATFORM_TYPES = ["forum", "community", "qa", "social", "blog", "other"];

// ---- newline-separated textarea <-> array helpers ------------------------
export const linesToArray = (text) =>
  String(text || "")
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean);

export const arrayToLines = (arr) => (Array.isArray(arr) ? arr.join("\n") : "");

// An empty edit-form value (used by "Add channel").
export const emptyChannel = (brandId) => ({
  brand_id: brandId || "",
  name: "",
  platformType: "community",
  homeUrl: "",
  enabled: true,
  loginUrl: "",
  username: "",
  password: "",
  loginNotes: "",
  searchUrls: [],
  postUrls: [],
  replyUrls: [],
  crawlEnabled: true,
  crawlIntervalDays: 1,
});

// =========================================================================
// Presentational subcomponents (pure — safe to unit-test in isolation)
// =========================================================================

// A single platform "card" in the list. Clicking it opens the edit view.
export function ChannelCard({ channel, onOpen }) {
  if (!channel) return null;
  const topics = channel.topics || [];
  const newTopics = topics.filter((t) => t.status === "new").length;
  return (
    <div
      className="border rounded-3 bg-white p-3 mb-2 cursor-pointer custom-channel-card"
      onClick={() => onOpen && onOpen(channel)}
    >
      <div className="d-flex align-items-center">
        <strong className="f-14">{channel.name || "(unnamed)"}</strong>
        <span className="badge badge-light ml-2 text-capitalize">{channel.platformType}</span>
        {channel.enabled === false && (
          <span className="badge badge-secondary ml-2">disabled</span>
        )}
        {channel.source === "auto" && (
          <span className="badge badge-info ml-2" title="Discovered by the weekly internet crawl">
            auto-found
          </span>
        )}
      </div>
      {channel.homeUrl && (
        <div className="text-muted small mt-1 text-truncate">{channel.homeUrl}</div>
      )}
      <div className="small mt-1">
        <span className="mr-3">{topics.length} topics</span>
        {newTopics > 0 && <span className="text-bid mr-3">{newTopics} new</span>}
        <span className="mr-3">{(channel.replyHistory || []).length} replies</span>
        {channel.lastCrawledAt && (
          <span className="text-muted">
            crawled {new Date(channel.lastCrawledAt).toLocaleDateString()}
          </span>
        )}
      </div>
    </div>
  );
}

// The edit view — a sibling of the brand-settings editor. Controlled: the
// parent owns `value` and we call onChange(field, newValue) on every edit.
export function ChannelEditForm({ value, onChange, hasPassword }) {
  const v = value || {};
  const set = (field) => (e) => {
    const el = e.target;
    const next = el.type === "checkbox" ? el.checked : el.value;
    onChange && onChange(field, next);
  };
  const setNum = (field) => (e) => onChange && onChange(field, Number(e.target.value) || 0);
  const setLines = (field) => (e) => onChange && onChange(field, linesToArray(e.target.value));

  return (
    <div className="custom-channel-edit">
      <div className="form-group-div mb-2">
        <label className="inter pl-2 f-14">Platform name</label>
        <input
          aria-label="Platform name"
          className="form-control"
          value={v.name || ""}
          onChange={set("name")}
          placeholder="e.g. Skool — Indie Founders"
        />
      </div>

      <div className="form-group-div mb-2">
        <label className="inter pl-2 f-14">Platform type</label>
        <select aria-label="Platform type" className="form-control" value={v.platformType || "community"} onChange={set("platformType")}>
          {PLATFORM_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group-div mb-2">
        <label className="inter pl-2 f-14">Home URL</label>
        <input aria-label="Home URL" className="form-control" value={v.homeUrl || ""} onChange={set("homeUrl")} placeholder="https://…" />
      </div>

      <hr />
      <div className="small text-muted pl-2 mb-2">Login — used by the worker to post replies. Password is encrypted at rest.</div>

      <div className="form-group-div mb-2">
        <label className="inter pl-2 f-14">Login URL</label>
        <input aria-label="Login URL" className="form-control" value={v.loginUrl || ""} onChange={set("loginUrl")} placeholder="https://…/login" />
      </div>
      <div className="form-group-div mb-2">
        <label className="inter pl-2 f-14">Username / email</label>
        <input aria-label="Username" className="form-control" value={v.username || ""} onChange={set("username")} />
      </div>
      <div className="form-group-div mb-2">
        <label className="inter pl-2 f-14">Password</label>
        <input
          aria-label="Password"
          type="password"
          className="form-control"
          value={v.password || ""}
          onChange={set("password")}
          placeholder={hasPassword ? "•••••• (leave blank to keep)" : "set a password"}
        />
      </div>
      <div className="form-group-div mb-2">
        <label className="inter pl-2 f-14">Login notes</label>
        <textarea aria-label="Login notes" className="form-control" rows={2} value={v.loginNotes || ""} onChange={set("loginNotes")} />
      </div>

      <hr />
      <div className="form-group-div mb-2">
        <label className="inter pl-2 f-14">Search URLs (one per line)</label>
        <textarea
          aria-label="Search URLs"
          className="form-control"
          rows={2}
          value={arrayToLines(v.searchUrls)}
          onChange={setLines("searchUrls")}
        />
      </div>
      <div className="form-group-div mb-2">
        <label className="inter pl-2 f-14">Post URLs (one per line)</label>
        <textarea aria-label="Post URLs" className="form-control" rows={2} value={arrayToLines(v.postUrls)} onChange={setLines("postUrls")} />
      </div>
      <div className="form-group-div mb-2">
        <label className="inter pl-2 f-14">Reply URLs (one per line)</label>
        <textarea aria-label="Reply URLs" className="form-control" rows={2} value={arrayToLines(v.replyUrls)} onChange={setLines("replyUrls")} />
      </div>

      <hr />
      <div className="d-flex align-items-center mb-2">
        <label className="inter pl-2 f-14 mb-0 mr-3">
          <input type="checkbox" className="mr-1" checked={v.enabled !== false} onChange={set("enabled")} aria-label="Enabled" />
          Enabled
        </label>
        <label className="inter pl-2 f-14 mb-0 mr-3">
          <input type="checkbox" className="mr-1" checked={v.crawlEnabled !== false} onChange={set("crawlEnabled")} aria-label="Crawl enabled" />
          Daily crawl
        </label>
        <label className="inter pl-2 f-14 mb-0">
          Every
          <input
            aria-label="Crawl interval days"
            type="number"
            min={1}
            className="form-control d-inline-block mx-1"
            style={{ width: 64 }}
            value={v.crawlIntervalDays || 1}
            onChange={setNum("crawlIntervalDays")}
          />
          day(s)
        </label>
      </div>
    </div>
  );
}

// One crawler-found topic with quick actions.
export function TopicRow({ topic, onCompose }) {
  if (!topic) return null;
  return (
    <div className="border-bottom py-2 custom-channel-topic">
      <div className="d-flex align-items-center">
        <a href={topic.url} target="_blank" rel="noreferrer" className="f-14 text-truncate">
          {topic.title || topic.url}
        </a>
        {topic.relevance > 0 && (
          <span className="badge badge-light ml-2" title="relevance score">
            {Math.round(topic.relevance * 100) / 100}
          </span>
        )}
        <span className="badge badge-light ml-2 text-capitalize">{topic.status}</span>
        <button type="button" className="btn btn-sm btn-outline-bid ml-auto" onClick={() => onCompose && onCompose(topic)}>
          Reply
        </button>
      </div>
      {topic.summary && <div className="small text-muted mt-1">{topic.summary}</div>}
    </div>
  );
}

// The reply composer — a textarea with an "AI draft" button NEXT TO "Send"
// (the ticket's core ask). onAiReply asks the worker for an LLM draft;
// onSend posts the reviewed reply to the platform.
export function ReplyComposer({ value, onChange, onAiReply, onSend, aiBusy, sendBusy, postUrl }) {
  return (
    <div className="custom-channel-composer border rounded-3 p-2 mt-2 bg-light">
      {postUrl && <div className="small text-muted mb-1 text-truncate">Replying on: {postUrl}</div>}
      <textarea
        aria-label="Reply text"
        className="form-control"
        rows={4}
        value={value || ""}
        onChange={(e) => onChange && onChange(e.target.value)}
        placeholder="Write your reply, or click AI draft to generate one…"
      />
      <div className="d-flex mt-2">
        <button
          type="button"
          className="btn btn-sm btn-outline-bid mr-2 custom-channel-ai-btn"
          onClick={() => onAiReply && onAiReply()}
          disabled={aiBusy}
        >
          {aiBusy ? "Drafting…" : "AI draft"}
        </button>
        <button
          type="button"
          className="btn btn-sm btn-bid text-white ml-auto custom-channel-send-btn"
          onClick={() => onSend && onSend()}
          disabled={sendBusy || !String(value || "").trim()}
        >
          {sendBusy ? "Sending…" : "Send"}
        </button>
      </div>
    </div>
  );
}

// The monitoring tab — every reply we've drafted/posted, newest first.
export function ReplyHistoryList({ history }) {
  const rows = (history || []).slice().sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  if (!rows.length) return <div className="text-muted small p-2">No replies yet.</div>;
  return (
    <div className="custom-channel-history">
      {rows.map((r, i) => (
        <div key={r._id || i} className="border-bottom py-2">
          <div className="d-flex align-items-center">
            <span className="badge badge-light text-capitalize mr-2">{r.source}</span>
            <span
              className={`badge mr-2 text-capitalize ${
                r.status === "sent" ? "badge-success" : r.status === "failed" ? "badge-danger" : "badge-secondary"
              }`}
            >
              {r.status}
            </span>
            {(r.postUrl || r.topicUrl) && (
              <a href={r.postUrl || r.topicUrl} target="_blank" rel="noreferrer" className="small text-truncate">
                {r.postUrl || r.topicUrl}
              </a>
            )}
            {r.createdAt && (
              <span className="text-muted small ml-auto">{new Date(r.createdAt).toLocaleString()}</span>
            )}
          </div>
          {r.replyText && <div className="small mt-1">{r.replyText}</div>}
          {r.error && <div className="small text-danger mt-1">{r.error}</div>}
        </div>
      ))}
    </div>
  );
}

// =========================================================================
// Container modal
// =========================================================================

export default function CustomChannelsModal({ isOpen, onClose, brandId, brandName }) {
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [discovering, setDiscovering] = useState(false);
  const [selected, setSelected] = useState(null); // channel being edited (or new)
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("settings"); // settings | topics | history
  const [composeTopic, setComposeTopic] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [sendBusy, setSendBusy] = useState(false);

  const refresh = useCallback(async () => {
    if (!brandId) return;
    setLoading(true);
    try {
      const res = await listCustomChannels(brandId);
      setChannels((res && res.data) || []);
    } catch (e) {
      addNotification(e.error || e.message || "Failed to load channels", "danger");
    } finally {
      setLoading(false);
    }
  }, [brandId]);

  useEffect(() => {
    if (isOpen) refresh();
  }, [isOpen, refresh]);

  const openChannel = (channel) => {
    setSelected(channel);
    setForm({ ...channel, password: "" });
    setTab("settings");
    setComposeTopic(null);
    setReplyText("");
  };

  const openNew = () => {
    const blank = emptyChannel(brandId);
    setSelected(blank);
    setForm(blank);
    setTab("settings");
  };

  const closeEditor = () => {
    setSelected(null);
    setForm(null);
    setComposeTopic(null);
  };

  const onFormChange = (field, val) => setForm((f) => ({ ...f, [field]: val }));

  const save = async () => {
    if (!form || !form.name) {
      addNotification("Platform name is required", "warning");
      return;
    }
    setSaving(true);
    try {
      // Don't send an empty password — the server keeps the existing one.
      const payload = { ...form, brand_id: brandId };
      if (!payload.password) delete payload.password;
      if (selected && selected._id) {
        await updateCustomChannel(selected._id, payload);
      } else {
        await createCustomChannel(payload);
      }
      addNotification("Channel saved", "success");
      await refresh();
      closeEditor();
    } catch (e) {
      addNotification(e.error || e.message || "Save failed", "danger");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!selected || !selected._id) return;
    setSaving(true);
    try {
      await deleteCustomChannel(selected._id);
      addNotification("Channel removed", "success");
      await refresh();
      closeEditor();
    } catch (e) {
      addNotification(e.error || e.message || "Delete failed", "danger");
    } finally {
      setSaving(false);
    }
  };

  const discover = async () => {
    setDiscovering(true);
    try {
      await discoverCustomChannels(brandId);
      addNotification("Internet discovery queued — new platforms will appear shortly", "success");
    } catch (e) {
      addNotification(e.error || e.message || "Discovery failed", "danger");
    } finally {
      setDiscovering(false);
    }
  };

  const crawlNow = async () => {
    if (!selected || !selected._id) return;
    try {
      await crawlCustomChannel(selected._id);
      addNotification("Crawl queued — relevant topics will appear shortly", "success");
    } catch (e) {
      addNotification(e.error || e.message || "Crawl failed", "danger");
    }
  };

  const reloadSelected = async () => {
    if (!selected || !selected._id) return;
    try {
      const res = await getCustomChannel(selected._id);
      if (res && res.data) {
        setSelected(res.data);
        setForm((f) => ({ ...(f || {}), topics: res.data.topics, replyHistory: res.data.replyHistory }));
      }
    } catch (_) {
      /* non-fatal */
    }
  };

  const aiDraft = async () => {
    if (!selected || !selected._id || !composeTopic) return;
    setAiBusy(true);
    try {
      await requestAiReply(selected._id, {
        postUrl: composeTopic.url,
        topicUrl: composeTopic.url,
        instructions: replyText, // operator hints become the AI brief
      });
      addNotification("AI draft requested — refresh History in a moment to review it", "success");
    } catch (e) {
      addNotification(e.error || e.message || "AI draft failed", "danger");
    } finally {
      setAiBusy(false);
    }
  };

  const send = async () => {
    if (!selected || !selected._id || !composeTopic) return;
    if (!replyText.trim()) {
      addNotification("Reply text is empty", "warning");
      return;
    }
    setSendBusy(true);
    try {
      // Create a manual reply row, then post it.
      const res = await requestAiReply(selected._id, {
        postUrl: composeTopic.url,
        topicUrl: composeTopic.url,
      });
      const replyId = res && res.data && res.data.replyId;
      if (replyId) {
        await sendReply(selected._id, replyId, { replyText });
      }
      addNotification("Reply queued for posting", "success");
      setReplyText("");
      setComposeTopic(null);
      await reloadSelected();
    } catch (e) {
      addNotification(e.error || e.message || "Send failed", "danger");
    } finally {
      setSendBusy(false);
    }
  };

  const tabBtn = (id, label) => (
    <button
      type="button"
      className={`btn btn-sm mr-1 ${tab === id ? "btn-bid text-white" : "btn-outline-bid"}`}
      onClick={() => {
        setTab(id);
        if (id === "history") reloadSelected();
      }}
    >
      {label}
    </button>
  );

  return (
    <MDBModal isOpen={isOpen} toggle={onClose} size="lg">
      <MDBModalHeader toggle={onClose}>
        Custom Channels{brandName ? ` — ${brandName}` : ""}
      </MDBModalHeader>
      <MDBModalBody>
        {!selected && (
          <div>
            <div className="d-flex mb-3">
              <button type="button" className="btn btn-sm btn-bid text-white mr-2" onClick={openNew}>
                + Add channel
              </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-bid"
                onClick={discover}
                disabled={discovering}
                title="Crawl the internet for platforms (forums, Q&A, communities) relevant to this brand"
              >
                {discovering ? "Discovering…" : "Discover platforms"}
              </button>
              <button type="button" className="btn btn-sm btn-link ml-auto" onClick={refresh}>
                Refresh
              </button>
            </div>
            {loading && <div className="text-muted small">Loading…</div>}
            {!loading && channels.length === 0 && (
              <div className="text-muted small">
                No custom channels yet. Add one manually, or click "Discover platforms" to let the
                weekly internet crawl find communities for this brand.
              </div>
            )}
            {channels.map((c) => (
              <ChannelCard key={c._id} channel={c} onOpen={openChannel} />
            ))}
          </div>
        )}

        {selected && (
          <div>
            <div className="d-flex align-items-center mb-3">
              <button type="button" className="btn btn-sm btn-link pl-0" onClick={closeEditor}>
                ← Back
              </button>
              <div className="ml-auto">
                {tabBtn("settings", "Settings")}
                {selected._id && tabBtn("topics", "Topics")}
                {selected._id && tabBtn("history", "History")}
              </div>
            </div>

            {tab === "settings" && (
              <div>
                <ChannelEditForm value={form} onChange={onFormChange} hasPassword={!!selected._hasPassword} />
                <div className="d-flex mt-3">
                  <button type="button" className="btn btn-sm btn-bid text-white mr-2" onClick={save} disabled={saving}>
                    {saving ? "Saving…" : "Save"}
                  </button>
                  {selected._id && (
                    <button type="button" className="btn btn-sm btn-outline-bid mr-2" onClick={crawlNow}>
                      Crawl now
                    </button>
                  )}
                  {selected._id && (
                    <button type="button" className="btn btn-sm btn-outline-danger ml-auto" onClick={remove} disabled={saving}>
                      Delete
                    </button>
                  )}
                </div>
              </div>
            )}

            {tab === "topics" && (
              <div>
                <div className="d-flex mb-2">
                  <span className="small text-muted">Relevant threads found by the crawler.</span>
                  <button type="button" className="btn btn-sm btn-link ml-auto" onClick={reloadSelected}>
                    Refresh
                  </button>
                </div>
                {(selected.topics || []).length === 0 && (
                  <div className="text-muted small">No topics yet — run "Crawl now" from Settings.</div>
                )}
                {(selected.topics || []).map((t) => (
                  <TopicRow key={t._id || t.url} topic={t} onCompose={(topic) => { setComposeTopic(topic); setReplyText(""); }} />
                ))}
                {composeTopic && (
                  <ReplyComposer
                    value={replyText}
                    postUrl={composeTopic.url}
                    onChange={setReplyText}
                    onAiReply={aiDraft}
                    onSend={send}
                    aiBusy={aiBusy}
                    sendBusy={sendBusy}
                  />
                )}
              </div>
            )}

            {tab === "history" && (
              <div>
                <div className="d-flex mb-2">
                  <span className="small text-muted">Every reply drafted or posted — for monitoring.</span>
                  <button type="button" className="btn btn-sm btn-link ml-auto" onClick={reloadSelected}>
                    Refresh
                  </button>
                </div>
                <ReplyHistoryList history={selected.replyHistory} />
              </div>
            )}
          </div>
        )}
      </MDBModalBody>
    </MDBModal>
  );
}
